import { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    CircularProgress,
    Snackbar,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import EditIcon from '@material-ui/icons/Edit';
import CommonServiceApi from '../../services/common-service';

interface CounterRecord {
    companyId: number;
    moduleName: string;
    counter: number;
}

interface EditModalState {
    open: boolean;
    moduleName: string;
    currentValue: number;
    newValue: string;
    error: string;
}

interface InvoiceCounterSettingsProps {
    companyId: number | null;
}

const MODULE_LABELS: Record<string, string> = {
    TICKET: 'Ticket',
    INVOICE: 'Invoice',
};

const api = new CommonServiceApi();

export default function InvoiceCounterSettings({ companyId }: InvoiceCounterSettingsProps) {
    const [counters, setCounters] = useState<CounterRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [snackBar, setSnackBar] = useState('');
    const [snackMsg, setSnackMsg] = useState('');

    const [editModal, setEditModal] = useState<EditModalState>({
        open: false,
        moduleName: '',
        currentValue: 0,
        newValue: '',
        error: '',
    });
    const [isUpdatingCounter, setIsUpdatingCounter] = useState(false);

    const loadCounters = async (id: number) => {
        setIsLoading(true);
        setCounters([]);
        try {
            const result = await api.getEntityCounters(id);
            const payload = result?.result ?? result?.data ?? result;
            const rawCounters: any[] = Array.isArray(payload?.counters) ? payload.counters
                : Array.isArray(payload) ? payload : [];
            const records: CounterRecord[] = rawCounters.map((r) => ({
                companyId: r.companyId ?? r.company_id,
                moduleName: r.moduleName ?? r.module_name,
                counter: r.counter,
            }));
            setCounters(records);
        } catch (err: any) {
            setSnackBar('error');
            setSnackMsg('Failed to load counters.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) {
            loadCounters(companyId);
        } else {
            setCounters([]);
        }
    }, [companyId]);

    const openEditModal = (record: CounterRecord) => {
        setEditModal({ open: true, moduleName: record.moduleName, currentValue: record.counter, newValue: '', error: '' });
    };

    const closeEditModal = () => {
        setEditModal({ open: false, moduleName: '', currentValue: 0, newValue: '', error: '' });
    };

    const handleCounterInputChange = (value: string) => {
        setEditModal((prev) => {
            const parsed = parseInt(value, 10);
            let error = '';
            if (value !== '' && (isNaN(parsed) || parsed <= prev.currentValue)) {
                error = `New value must be greater than current value (${prev.currentValue})`;
            }
            return { ...prev, newValue: value, error };
        });
    };

    const handleConfirmCounterUpdate = async () => {
        if (!companyId) return;
        const parsed = parseInt(editModal.newValue, 10);
        if (isNaN(parsed) || parsed <= editModal.currentValue) {
            setEditModal((prev) => ({ ...prev, error: `New value must be greater than current value (${prev.currentValue})` }));
            return;
        }
        setIsUpdatingCounter(true);
        try {
            await api.updateEntityCounter({ companyId, moduleName: editModal.moduleName, counter: parsed });
            await loadCounters(companyId);
            setSnackBar('success');
            setSnackMsg(`Counter for ${MODULE_LABELS[editModal.moduleName] || editModal.moduleName} updated to ${parsed}.`);
            closeEditModal();
        } catch (err: any) {
            setSnackBar('error');
            setSnackMsg(`Failed to update counter: ${err?.message || err}`);
        } finally {
            setIsUpdatingCounter(false);
        }
    };

    const expectedTypes = ['INVOICE', 'TICKET'];

    const tableRows = expectedTypes.map((moduleName) => {
        const found = counters.find((c) => c.moduleName === moduleName);
        return { moduleName, counter: found?.counter ?? null, companyId: companyId! };
    });

    if (!companyId) return null;

    return (
        <>
            <Typography variant="h6" style={{ marginTop: 32, marginBottom: 12, fontWeight: 600 }}>
                Counter Settings
            </Typography>

            {isLoading && <CircularProgress size={24} />}

            {!isLoading && (
                <div style={{ marginBottom: 24 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#333' }}>
                        Current Counters
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow style={{ background: '#f5f5f5' }}>
                                    <TableCell><strong>Entity Type</strong></TableCell>
                                    <TableCell><strong>Counter Value</strong></TableCell>
                                    <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableRows.map((row) => {
                                    return (
                                        <TableRow key={row.moduleName}>
                                            <TableCell>{MODULE_LABELS[row.moduleName] || row.moduleName}</TableCell>
                                            <TableCell>
                                                {row.counter !== null
                                                    ? <strong>{row.counter.toLocaleString()}</strong>
                                                    : <span style={{ color: '#aaa', fontStyle: 'italic' }}>Not set</span>}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => openEditModal({ moduleName: row.moduleName, counter: row.counter ?? 0, companyId: row.companyId })}
                                                >
                                                    {row.counter !== null ? 'Edit' : 'Set'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}

            <Dialog open={editModal.open} onClose={closeEditModal} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Counter — {MODULE_LABELS[editModal.moduleName] || editModal.moduleName}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ marginBottom: 16, color: '#555' }}>
                        Current value: <strong>{editModal.currentValue.toLocaleString()}</strong>
                    </Typography>
                    <TextField
                        label="New Counter Value"
                        type="number"
                        variant="outlined"
                        fullWidth
                        value={editModal.newValue}
                        onChange={(e) => handleCounterInputChange(e.target.value)}
                        error={!!editModal.error}
                        helperText={editModal.error || `New value must be greater than current value (${editModal.currentValue})`}
                        inputProps={{ min: editModal.currentValue + 1 }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEditModal} disabled={isUpdatingCounter}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmCounterUpdate} disabled={isUpdatingCounter || !editModal.newValue || !!editModal.error}>
                        {isUpdatingCounter ? <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} /> : null}
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackBar !== ''} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} autoHideDuration={6000} onClose={() => setSnackBar('')}>
                <Alert severity={snackBar === 'error' ? 'error' : 'success'} onClose={() => setSnackBar('')}>
                    {snackMsg}
                </Alert>
            </Snackbar>
        </>
    );
}
